/**
 * Kitchen-measure vocabulary — the ONE place that decides what a unit means.
 *
 * Lives in `@ordo/constants` (zero-dependency, browser-safe) because both
 * halves of the fleet need identical answers and neither can import the
 * other's copy: `packages/schema` aggregates purchasing server-side, while
 * `apps/ordo`'s client merges the same demand for the kitchen print packet.
 * When those two disagree about what an ounce weighs, chefs get two different
 * numbers for one week of food — see the 2026-08-06 Classic Catering incident,
 * where the shopping list reported 20972.25 oz and the print packet split the
 * same demand into `20945 g` + `27.25 oz`, against 47.9 lb of real need.
 *
 * The rule this file encodes: two amounts may be added together ONLY when they
 * share a dimension. Grams and fluid ounces need a per-ingredient density to
 * combine, and a density is never guessed.
 */

/** US-customary cups per one unit of each recognized volume unit. */
const VOLUME_UNIT_TO_CUPS: Record<string, number> = {
  cup: 1,
  cups: 1,
  c: 1,
  tbsp: 1 / 16,
  tbs: 1 / 16,
  tablespoon: 1 / 16,
  tablespoons: 1 / 16,
  tsp: 1 / 48,
  teaspoon: 1 / 48,
  teaspoons: 1 / 48,
  floz: 1 / 8,
  'fl oz': 1 / 8,
  'fl. oz': 1 / 8,
  'fl.oz': 1 / 8,
  'fluid ounce': 1 / 8,
  'fluid ounces': 1 / 8,
  pt: 2,
  pint: 2,
  pints: 2,
  qt: 4,
  quart: 4,
  quarts: 4,
  gal: 16,
  gallon: 16,
  gallons: 16,
  ml: 1 / 236.588,
  milliliter: 1 / 236.588,
  milliliters: 1 / 236.588,
  millilitre: 1 / 236.588,
  millilitres: 1 / 236.588,
  l: 1000 / 236.588,
  liter: 1000 / 236.588,
  liters: 1000 / 236.588,
  litre: 1000 / 236.588,
  litres: 1000 / 236.588,
}

/** Grams per one unit of each recognized weight unit. */
const WEIGHT_UNIT_TO_G: Record<string, number> = {
  g: 1,
  gram: 1,
  grams: 1,
  kg: 1000,
  kilogram: 1000,
  kilograms: 1000,
  mg: 0.001,
  milligram: 0.001,
  milligrams: 0.001,
  oz: 28.3495,
  ounce: 28.3495,
  ounces: 28.3495,
  lb: 453.592,
  lbs: 453.592,
  pound: 453.592,
  pounds: 453.592,
}

/** Lowercase + trim + collapse whitespace so "Fl  Oz." matches "fl oz". */
export function normalizeMeasureUnit(unit: string): string {
  return unit.toLowerCase().trim().replace(/\s+/g, ' ').replace(/\.$/, '')
}

/** True when `unit` names a recognized US/metric volume unit. */
export function isVolumeUnit(unit: string): boolean {
  return VOLUME_UNIT_TO_CUPS[normalizeMeasureUnit(unit)] !== undefined
}

/** True when `unit` names a recognized weight unit. */
export function isWeightUnit(unit: string): boolean {
  return WEIGHT_UNIT_TO_G[normalizeMeasureUnit(unit)] !== undefined
}

/**
 * Convert a volume quantity to US cups, or null when the unit isn't a
 * recognized volume unit. Cups are the pivot because both the CNDB weight
 * measures and USDA meal-pattern crediting express volumes in cups.
 */
export function volumeToCups(value: number, unit: string): number | null {
  const factor = VOLUME_UNIT_TO_CUPS[normalizeMeasureUnit(unit)]
  if (factor === undefined) {
    return null
  }
  return value * factor
}

/**
 * Convert a weight quantity to grams, or null when the unit isn't a recognized
 * weight unit. Grams are the pivot for the same reason cups are for volume:
 * every CNDB weight measure is expressed in them.
 */
export function weightToGrams(value: number, unit: string): number | null {
  const factor = WEIGHT_UNIT_TO_G[normalizeMeasureUnit(unit)]
  if (factor === undefined) {
    return null
  }
  return value * factor
}

/**
 * The three measurement families a recipe amount can belong to.
 *
 * Note `oz` is WEIGHT and `fl oz` is VOLUME — the single most common
 * authoring confusion in this domain, and the reason the two are never
 * inferred from a shared "oz" prefix.
 */
export type MeasureDimension = 'WEIGHT' | 'VOLUME' | 'COUNT'

/**
 * Which family a unit belongs to. Anything unrecognized — free-text package
 * labels like "Bowlpak (1 oz)" or "Banana" — is COUNT, a dimensionless tally.
 * Such a label may embed a unit in its text; that text is descriptive and
 * must never be parsed into a dimension here.
 */
export function measureDimension(unit: string): MeasureDimension {
  const u = normalizeMeasureUnit(unit)
  if (WEIGHT_UNIT_TO_G[u] !== undefined) {
    return 'WEIGHT'
  }
  if (VOLUME_UNIT_TO_CUPS[u] !== undefined) {
    return 'VOLUME'
  }
  return 'COUNT'
}

/**
 * Factor converting one `from` into one `to`, or null when the two units are
 * not in the same dimension (and therefore must never be summed).
 *
 * COUNT units convert only to themselves: "each" and "slices" are both
 * dimensionless, but six slices is not six eaches.
 */
export function sameDimensionFactor(from: string, to: string): number | null {
  const fg = weightToGrams(1, from)
  const tg = weightToGrams(1, to)
  if (fg !== null && tg !== null) {
    return fg / tg
  }
  const fc = volumeToCups(1, from)
  const tc = volumeToCups(1, to)
  if (fc !== null && tc !== null) {
    return fc / tc
  }
  return normalizeMeasureUnit(from) === normalizeMeasureUnit(to) ? 1 : null
}

/**
 * Group key for "may these two amounts be added together?".
 *
 * WEIGHT and VOLUME each collapse to one group because every unit inside them
 * converts exactly. COUNT does not, so each distinct label keeps its own group.
 * Callers use this as (part of) an aggregation key; amounts sharing a key are
 * guaranteed convertible via {@link sameDimensionFactor}.
 */
export function measureGroupKey(unit: string): string {
  const dimension = measureDimension(unit)
  return dimension === 'COUNT' ? `COUNT:${normalizeMeasureUnit(unit)}` : dimension
}

/**
 * Restate `value` (in `from`) as a quantity in `to`. Callers only reach this
 * for units already proven compatible by {@link measureGroupKey} — within a
 * group, a factor exists by construction. If no factor resolves, the
 * key/convert CONTRACT was violated and this THROWS: the old `?? 1` fallback
 * added the amount UNCONVERTED, producing a plausible-looking wrong number —
 * the exact split-fleet failure class this module exists to close. A crash
 * names the bug; a quiet wrong purchase quantity does not.
 */
export function convertMeasure(value: number, from: string, to: string): number {
  if (normalizeMeasureUnit(from) === normalizeMeasureUnit(to)) {
    return value
  }
  const factor = sameDimensionFactor(from, to)
  if (factor === null) {
    throw new Error(
      `convertMeasure: no conversion between "${from}" and "${to}" — ` +
        'caller must group amounts by measureGroupKey before converting',
    )
  }
  return value * factor
}

/**
 * Volume units that can appear embedded in a free-text count/package label
 * ("Cup (4.5 fl oz)", "4 fl oz carton"). Longer forms first so `\b` can't
 * strand on a shorter prefix. Weight "oz" deliberately does NOT match — only
 * the explicit fluid forms are volumes.
 */
const EMBEDDED_VOLUME_RE =
  /(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s*(fluid ounces?|fl\.?\s?oz|milliliters?|millilitres?|ml|liters?|litres?|cups?|tablespoons?|tbsp|teaspoons?|tsp)\b/i

/**
 * Numeric value of an embedded amount token: a mixed number ("1 1/2"), a
 * vulgar fraction ("1/4"), or a plain decimal ("4.5"). NaN when unparseable.
 *
 * Kitchen portions are written as fractions far more often than decimals, and
 * a digits-only pattern does not merely miss them — it SILENTLY MATCHES THE
 * DENOMINATOR. Before this existed, "1/4 cup" stranded on the leading "1",
 * failed, resumed at "4", and returned FOUR CUPS: a 16x over-read on a
 * portion label, feeding the USDA crediting rollup. "1/2 cup" read as 2 and
 * "3/4 cup" as 4. Every fraction was wrong, always upward.
 */
function parseAmountToken(token: string): number {
  const mixed = /^(\d+)\s+(\d+)\/(\d+)$/.exec(token)
  if (mixed) {
    const denominator = Number(mixed[3])
    return denominator === 0 ? Number.NaN : Number(mixed[1]) + Number(mixed[2]) / denominator
  }
  const fraction = /^(\d+)\/(\d+)$/.exec(token)
  if (fraction) {
    const denominator = Number(fraction[2])
    return denominator === 0 ? Number.NaN : Number(fraction[1]) / denominator
  }
  return Number(token)
}

/**
 * Extract a portion VOLUME embedded in a free-text quantity label and return
 * it in cups — the volume ONE such unit holds ("Cup (4.5 fl oz)" → 0.5625).
 * A fluid-ounce label is a volume, never a weight. Returns null when the label
 * carries no volume token.
 */
export function parseEmbeddedPortionVolumeCups(label: string): number | null {
  const match = EMBEDDED_VOLUME_RE.exec(label)
  if (!match) {
    return null
  }
  const value = parseAmountToken(match[1] ?? '')
  if (!Number.isFinite(value)) {
    return null
  }
  return volumeToCups(value, match[2] ?? '')
}
